import moment from 'moment-timezone'

const DEFAULT_ZONE = 'America/Vancouver'

// Hours contain timestamps whose clock components describe recurring local hours.
function atTime(value, day, zone) {
  const clock = moment(value).tz(zone)
  return day.clone().hour(clock.hour()).minute(clock.minute()).second(0).millisecond(0)
}

function serviceWindows(hours, day, zone) {
  const schedule = (hours || {})[day.day()] || {}
  if (!schedule.open || !schedule.close) return []
  const open = atTime(schedule.open, day, zone)
  const close = atTime(schedule.close, day, zone)
  if (!open.isValid() || !close.isValid() || close.isSame(open)) return []
  if (close.isBefore(open)) close.add(1, 'day')
  if (!schedule.breakStart || !schedule.breakEnd) return [[open, close]]
  const start = atTime(schedule.breakStart, day, zone)
  const end = atTime(schedule.breakEnd, day, zone)
  if (start.isBefore(open)) start.add(1, 'day')
  if (end.isBefore(start)) end.add(1, 'day')
  if (start.isSame(end)) return [[open, close]]
  if (!start.isValid() || !end.isValid() || !start.isBefore(close) || !end.isAfter(open)) return [[open, close]]
  return [[open, start, true], [end, close]].filter(([a, b]) => a.isBefore(b))
}

function parsePickupTime(value, timeZone = DEFAULT_ZONE, now = moment()) {
  const iso = moment(value, moment.ISO_8601, true).tz(timeZone)
  if (iso.isValid()) return iso
  // Older ordering clients send a local time and date without a year.
  const current = moment(now).tz(timeZone)
  const legacy = moment.tz(`${value}, ${current.year()}`, 'LT, MMM D, YYYY', true, timeZone)
  if (legacy.isValid() && legacy.isBefore(current.clone().startOf('day'))) {
    const nextYear = legacy.clone().add(1, 'year')
    if (nextYear.isBefore(current.clone().startOf('day').add(8, 'days'))) return nextYear
  }
  return legacy
}

function isPickupTimeValid({ hours, waitTime = 0, timeZone = DEFAULT_ZONE, orderTime, now = moment() }) {
  if (typeof orderTime !== 'string') return false
  const current = moment(now).tz(timeZone)
  const wait = Math.max(0, Number(waitTime) || 0)
  const asap = orderTime === 'ASAP'
  const target = asap ? current.clone().add(wait, 'minutes') : parsePickupTime(orderTime, timeZone, now)
  if (!target.isValid() || target.isBefore(current.clone().add(wait, 'minutes'))) return false
  if (target.isBefore(current.clone().startOf('day')) || !target.isBefore(current.clone().startOf('day').add(8, 'days'))) return false
  if (!asap && (target.minute() % 15 || target.second() || target.millisecond())) return false
  return [target.clone().startOf('day').subtract(1, 'day'), target.clone().startOf('day')].some(day =>
    serviceWindows(hours, day, timeZone).some(([open, close, excludesEnd]) =>
      target.isSameOrAfter(open.clone().add(wait, 'minutes')) && (excludesEnd ? target.isBefore(close) : target.isSameOrBefore(close)) &&
      (!asap || current.isSameOrAfter(open))
    )
  )
}

function pickupDates({ hours, waitTime = 0, timeZone = DEFAULT_ZONE, now = moment() }) {
  const current = moment(now).tz(timeZone)
  return Array.from({ length: 8 }, (_, index) => {
    const day = current.clone().startOf('day').add(index, 'days')
    const times = []
    const end = day.clone().add(1, 'day')
    for (let time = day.clone(); time.isBefore(end); time.add(15, 'minutes')) {
      const value = time.toISOString()
      if (isPickupTimeValid({ hours, waitTime, timeZone, now: current, orderTime: value })) times.push(value)
    }
    return { date: day.format('YYYY-MM-DD'), label: day.format('ddd, MMM D'), times }
  })
}

export { isPickupTimeValid, pickupDates, parsePickupTime }
